import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Body1 } from '@ui/Typography'

import { getContestPoints } from '@utils/format.utils'
import { getScrollToCardId } from '@utils/getItemId'

import { ClientContestCampaignDetailDto } from '@model/ClientContestCampaignDetailDto'
import { ClientContestCampaignResultDto } from '@model/ClientContestCampaignResultDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import styles from '../Rebate/CampaignRebateCard.module.scss'

interface CampaignRebateCardsProps {
  data: ClientContestCampaignResultDto[]
  campaignDetails: ClientContestCampaignDetailDto
  campaignContestPointsValue: string
  campaignContestPointsSymbol: string
}

export const CampaignContestCards: React.FC<CampaignRebateCardsProps> = (props) => {
  const { data, campaignDetails, campaignContestPointsValue, campaignContestPointsSymbol } = props

  const { t } = useTranslation()

  if (!data.length) {
    return (
      <Body1 isParagraph className='text-align-center pt-4'>
        {t('No results')}
      </Body1>
    )
  }

  return (
    <React.Fragment>
      {data.map((contestCampaign, index) => (
        <CampaignContestCard
          data={contestCampaign}
          campaignDetails={campaignDetails}
          campaignContestPointsValue={campaignContestPointsValue}
          campaignContestPointsSymbol={campaignContestPointsSymbol}
          key={`contestCampaign_${index}`}
          cardId={getScrollToCardId(index, data.length, 'campaign-results')}
        />
      ))}
    </React.Fragment>
  )
}

interface CampaignContestCardProps {
  data: ClientContestCampaignResultDto
  campaignDetails: ClientContestCampaignDetailDto
  campaignContestPointsValue: string
  campaignContestPointsSymbol: string
  cardId?: string
}

const CampaignContestCard: React.FC<CampaignContestCardProps> = (props) => {
  const { data, campaignDetails, campaignContestPointsValue, campaignContestPointsSymbol, cardId } =
    props

  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()

  return (
    <div
      className={classNames(styles.card, {
        [styles.resultCurrentRanking]: data.name === campaignDetails.tradingAccount.name,
      })}
      id={cardId}
    >
      <div className='is-flex is-justify-content-space-between'>
        <span className='has-text-weight-bold'>{t('Campaigns.Ranking')}</span>
        <span className='has-text-weight-bold'>
          {!isDisqualifiedRanking(data) ? `#${data.ranking}` : t('N/A')}
        </span>
      </div>

      <div className={styles.subtitle}>
        <span className='text-secondary'>{t('Campaigns.Account Number')}</span>
        <span>{data.name}</span>
      </div>

      <div className={styles.subtitle}>
        <span className='text-secondary'> {campaignContestPointsValue}</span>
        <span>{getContestPoints(data.points, campaignContestPointsSymbol, formatMoney)}</span>
      </div>
    </div>
  )
}

const isDisqualifiedRanking = (data: ClientContestCampaignResultDto) => {
  return data.ranking === -1
}
